import { reactive, ref, shallowRef, watch, nextTick } from 'vue'
import * as calc from './calc.js';
import { store, genId, createDefaultRecord, isCalcFresh } from './setup_store.js';
import { readJSON, writeJSON } from './use_persisted.js';
import { scopedKey } from './setup_keys.js';

// Which setup `inputs`/`computedResult` currently mirror — null until
// loadSetup() below has run once. Set by loadSetup() rather than passed
// around, since the persistence watcher further down needs to know the
// current id at write time, and exportSetupJson()/applySetupData() need it
// to tell whether a given setup id is the "live" one (mirrored into the
// reactive `inputs`/`computedResult` below) or one that's only sitting in
// the store right now, not currently open for editing.
let currentSetupId = null;

const inputs = reactive({
  marks: [],
  sight_settings: new calc.SightSettings(),
  // Free-text description of this setup. Deliberately left out of
  // snapshotInputs() below (the string undo/redo and isStale both compare
  // against) — it's persisted and synced like sight_settings/marks, but
  // never creates an undo step and never marks a compute stale. Its own
  // edit history lives in the setup log instead (see setup_log.js).
  description: '',
});

const computedResult = shallowRef(new calc.Calculated());
const isStale = ref(true);

// The active setup's own single "last changed" timestamp — the one thing
// sync.js's last-write-wins comparison uses. Deliberately separate from
// sight_settings.updated_at/marks[].updated_at, which exist purely for the
// user's own "updated 3m ago" labels (input_sight_settings.vue/
// input_marks.vue) and don't cover everything that makes a setup "changed"
// anyway. One clear field beats deriving a sync timestamp by scanning
// scattered per-field ones every time.
const setupUpdatedAt = ref(0);

export function touchActiveSetup() {
  setupUpdatedAt.value = Date.now();
}

// Set around a wholesale reload of `inputs` (loadSetup switching setups,
// applySetupData pulling in a remote/imported setup) — those already know
// and set the correct setupUpdatedAt themselves (the setup's own stored
// timestamp, or the remote's), so the auto-touch watcher below must not
// also stamp its own Date.now() over that just because inputs changed.
let suppressAutoTouch = false;

// Notified once a compute genuinely finishes (not on every isStale/
// computedResult change — loadSetup/applySetupData also touch those when
// switching setups, which shouldn't trigger this). sync.js subscribes here
// to push a fresh result immediately rather than waiting out any debounce —
// unlike a burst of keystrokes, a finished compute is a single, discrete
// event with nothing more coming right behind it.
const computeFinishedListeners = new Set();
export function onComputeFinished(fn) {
  computeFinishedListeners.add(fn);
}

// Notified whenever loadSetup() replaces `inputs` wholesale (a setup
// switch, or the initial load on startup) — passed the setup id that just
// became active. setup_log.js subscribes here to reset its "last logged
// description" baseline per setup, so switching setups (or reloading the
// page) never looks like the user editing the description.
const setupLoadedListeners = new Set();
export function onSetupLoaded(fn) {
  setupLoadedListeners.add(fn);
}

// Excludes updated_at — this snapshot is compared for equality to decide
// isStale and undo/redo steps, both of which care only about the physical
// calibration values. Including updated_at would mark things stale (or
// create a bogus undo step) purely from a timestamp ticking over, with no
// actual value having changed.
function snapshotInputs() {
  const { updated_at, ...sightSettingsSansTimestamp } = inputs.sight_settings;
  return JSON.stringify({
    sight_settings: sightSettingsSansTimestamp,
    marks: inputs.marks.map(({ updated_at, ...rest }) => rest),
  });
}

// Stamps "now" onto any sight setting or mark that doesn't have a
// last-changed timestamp yet — data saved before this field existed, or
// restored from an undo/redo snapshot (which never carries one, see
// snapshotInputs above). Called whenever inputs are replaced wholesale
// (loadSetup, applySnapshot) rather than edited field-by-field, since the
// normal edit handlers (touchSightSettings/touchMark) already stamp as
// they go.
export function backfillTimestamps({ sight_settings, marks }) {
  const now = Date.now();
  if (sight_settings.updated_at == null) sight_settings.updated_at = now;
  marks.forEach((m) => {
    if (m.updated_at == null) m.updated_at = now;
  });
}

// The inputs as they were at the last successful compute — null if nothing
// has been computed yet this load. Reset by loadSetup() on every switch.
let lastComputedSnapshot = null;

// isStale tracks whether the current inputs differ from the last successful
// compute — in both directions, so an edit that gets undone (typed away,
// then typed back to the original value) clears the stale flag again
// without needing another recompute. This is the only place isStale gets
// set from an input change; it used to also be forced true directly from
// each input's change handler, but that raced with this watcher depending
// on which ran last.
watch(inputs, () => {
  isStale.value = lastComputedSnapshot === null || snapshotInputs() !== lastComputedSnapshot;
}, { deep: true });

// Centralizes "this setup changed" so editing components (input_sight_
// settings.vue, input_marks.vue) don't each need to remember to call
// touchActiveSetup() themselves — any deep change to inputs bumps it here,
// once, covering every field edit, add/remove-row, sort, and undo/redo
// alike. Suppressed only for the wholesale reloads (loadSetup,
// applySetupData) that already carry their own correct timestamp.
watch(inputs, () => {
  if (!suppressAutoTouch) touchActiveSetup();
}, { deep: true });

// The one place that writes the active setup's data back into the store —
// every other write (rename, a setup that isn't currently open) mutates
// store.value.setups[id] directly (see setups.js/applySetupData below)
// since there's no live mirror to keep in sync for those. calc is folded in
// right here as one more field of the same record, rather than a second,
// parallel write path (see git history for the getSetupCalc/applySetupCalc
// pair this replaced) — a setup's precomputed fit is just data belonging to
// that setup, same as its sight_settings/marks.
watch(
  [inputs, isStale, computedResult, setupUpdatedAt],
  ([newInputs, newIsStale, newComputedResult, newSetupUpdatedAt]) => {
    if (!currentSetupId) return; // loadSetup() hasn't run yet

    const existing = store.value.setups[currentSetupId];
    store.value.setups[currentSetupId] = {
      name: existing?.name ?? 'Setup',
      sight_settings: newInputs.sight_settings,
      marks: newInputs.marks,
      description: newInputs.description,
      calc: newIsStale ? null : newComputedResult.toJSON(),
      updated_at: newSetupUpdatedAt,
      undo: existing?.undo ?? { stack: [], redo: [] },
    };
  },
  { deep: true }  // watch nested changes (e.g., marks.push())
)

// ── Undo/redo over the calibration inputs (sight settings + marks) ──
//
// Snapshots are committed on a debounce rather than on every keystroke —
// v-model fires on every character typed, and nobody wants to press undo
// once per digit to get back one intended edit. Editing coalesces into a
// single step once the user pauses; add/remove-row and unit-toggle clicks
// each land as their own step since those aren't part of a typing burst.
const UNDO_DEBOUNCE_MS = 600;
const MAX_UNDO_HISTORY = 50;

let undoStack = [];
let redoStack = [];
let undoDebounceTimer = null;
let lastCommittedSnapshot = snapshotInputs(); // reset by loadSetup() on every switch

const canUndo = ref(false);
const canRedo = ref(false);

function applyInputsData(parsed) {
  // Parse both before touching `inputs` — a Mark validation failure partway
  // through shouldn't leave sight_settings overwritten but marks untouched.
  const newSightSettings = calc.SightSettings.fromJson(parsed.sight_settings);
  const newMarks = calc.Marks.fromJson(parsed.marks).marks;
  Object.assign(inputs.sight_settings, newSightSettings);
  inputs.marks.splice(0, inputs.marks.length, ...newMarks);
}

function applySnapshot(json) {
  applyInputsData(JSON.parse(json));
  backfillTimestamps(inputs);
}

// Persisted inside the setup's own store record (store.value.setups[id].undo)
// so switching setups doesn't wipe history — undo/redo resume right where
// they were left for whichever setup you switch back to. Local-only,
// deliberately: exportSetupJson/applySetupData (the sync/file-export
// contract, see below) never touch this field, so it never leaves the
// browser.
function saveUndoHistory() {
  if (!currentSetupId) return;
  const record = store.value.setups[currentSetupId];
  if (record) record.undo = { stack: undoStack, redo: redoStack };
}

function commitUndoStep() {
  const current = snapshotInputs();
  if (current === lastCommittedSnapshot) return;

  undoStack.push(lastCommittedSnapshot);
  if (undoStack.length > MAX_UNDO_HISTORY) undoStack.shift();
  lastCommittedSnapshot = current;
  redoStack = [];
  canUndo.value = true;
  canRedo.value = false;
  saveUndoHistory();
}

watch(inputs, () => {
  clearTimeout(undoDebounceTimer);
  undoDebounceTimer = setTimeout(commitUndoStep, UNDO_DEBOUNCE_MS);
}, { deep: true });

function undo() {
  clearTimeout(undoDebounceTimer);

  // An edit still mid-debounce (not yet its own history entry) — undo just
  // discards it and drops back to the last settled state, rather than also
  // consuming a real history entry on top of that.
  const current = snapshotInputs();
  if (current !== lastCommittedSnapshot) {
    redoStack.push(current);
    applySnapshot(lastCommittedSnapshot);
    canRedo.value = true;
    canUndo.value = undoStack.length > 0;
    saveUndoHistory();
    return;
  }

  if (undoStack.length === 0) return;
  const previous = undoStack.pop();
  redoStack.push(lastCommittedSnapshot);
  applySnapshot(previous);
  lastCommittedSnapshot = previous;
  canUndo.value = undoStack.length > 0;
  canRedo.value = true;
  saveUndoHistory();
}

function redo() {
  clearTimeout(undoDebounceTimer);
  if (redoStack.length === 0) return;

  const next = redoStack.pop();
  undoStack.push(lastCommittedSnapshot);
  applySnapshot(next);
  lastCommittedSnapshot = next;
  canUndo.value = true;
  canRedo.value = redoStack.length > 0;
  saveUndoHistory();
}

// Set when the main compute (velocity/drag/offset fit) fails. Unlike a
// toast, this has no timer — it stays up until the inputs are fixed and
// compute() succeeds again.
const computeError = ref(null);

// True while a compute is actually running. calc.js's solver is fully
// synchronous, so once it starts it blocks the main thread until done —
// there's no "in progress" moment to observe from the outside. The only
// way to show a "computing" indicator at all is to flip this on and let
// the browser actually paint it *before* kicking off the blocking call.
const isComputing = ref(false);

function compute() {
  console.log('Calculating arrow flight');

  try {
    let data = calc.marksToM(inputs.marks);

    // findVelocityAndDrag already returns the offset/fit for its converged
    // (v, c) — no separate calcOffset call needed to recover them.
    let result = calc.findVelocityAndDrag(inputs.sight_settings, data);

    console.log(`Calculated v: ${result.v}, c: ${result.drag}, offset: ${result.offset}`);

    computedResult.value = result;

    lastComputedSnapshot = snapshotInputs();
    isStale.value = false;
    computeError.value = null;

    // A fresh calc is as much a change to this setup as an edited field —
    // without this, syncing a compute that finished *after* the edit that
    // prompted it already got pushed would carry the same updated_at as
    // that earlier push, and the server's last-write-wins guard
    // (existing.updated_at >= incoming) would silently keep its old,
    // pre-compute calc instead of accepting the new one.
    touchActiveSetup();
    computeFinishedListeners.forEach((fn) => fn());

  } catch (error) {
    console.log('error computing everything:', error.message);
    console.log(error.stack);
    computeError.value = error.message;
  }
}

// ── Shared level-ground (cut_angle 0) mark-vs-distance table ──
//
// graph.vue, full_marks.vue, cuts_table.vue, and mark.vue's own "Effective
// Distance" figure each used to independently sweep calculateMark over
// their own overlapping level-ground distance range every time they opened
// or recomputed. This builds it once, here — one call into calc.js's
// (wasm-backed) levelMarksTable, which does the whole sweep in Rust — and
// persists it so a page reload with an unchanged calibration costs nothing
// to redraw any of those panels from.
//
// Built at 0.25m steps — finer than any consumer's own display resolution
// — so a lookup at any distance (lookupLevelMark below) can linearly
// interpolate between two adjacent rows with error far below anything ever
// displayed; no consumer needs to land exactly on the table's own grid.
// cuts_table.vue and mark.vue don't use lookupLevelMark at all — their cut-
// distance search (calc.findCutDistance/calc.cutsGrid) runs entirely in
// Rust, reusing this same table without it ever crossing back into JS for
// that purpose.
export const LEVEL_TABLE_STEP = 0.25;

// In-memory copy of whatever getLevelMarksTable() last built or loaded —
// checked before localStorage so repeated calls within the same page load
// (e.g. Graph and Full Marks both opened back to back) don't even pay a
// JSON.parse. Cleared implicitly by simply never matching once the
// fingerprint moves on. (wasm also caches its own copy of this table, keyed
// the same way — see rust/calc-core/src/lib.rs — but that only saves
// rebuilding it in Rust; this still saves the serde-wasm-bindgen
// serialization a fresh wasm call would otherwise pay every time.)
let levelTableCache = null; // { setupId, fingerprint, rows }

// Everything that could change what the table contains — matches the same
// sight_settings|sight_bar_position|calculated shape the per-panel caches
// this replaces already used as their own cache keys (Calculated's
// toString() covers v/drag/offset/fit together). cut_angle isn't part of
// it: this table is always cut_angle 0 by construction.
function levelTableFingerprint() {
  return `${inputs.sight_settings}|${inputs.sight_settings.sight_bar_position}|${computedResult.value}`;
}

function levelTableStorageKey(setupId) {
  return scopedKey('level-marks-table', setupId);
}

// Returns an array of { distance, mark, angle, fps, t, drop, verticalMarks,
// horizontalMarks } rows (calculateMark's own return shape, plus distance)
// for the active setup, rebuilding (and re-persisting) only when the
// cached version's fingerprint no longer matches the current
// sight_settings/calculated — see this section's own header for why that
// guard is what keeps this from ever going stale under a caller.
//
// Async for the same reason as the main-branch version even though the
// underlying wasm call itself is synchronous — building the whole table is
// one opaque Rust call with no way to yield mid-computation the way a
// chunked JS loop can, so unlike every other chunked sweep in this
// codebase, a cold build here really does block the main thread for its
// whole duration (tens of milliseconds for a realistic bow, not the
// multi-frame stalls chunking exists to avoid) — a deliberate trade for
// how rarely it actually runs (once per real calibration change, not once
// per panel open) and how much cheaper the whole table is to build in
// Rust than in JS to begin with.
export async function getLevelMarksTable() {
  const setupId = currentSetupId;
  const fingerprint = levelTableFingerprint();

  if (levelTableCache && levelTableCache.setupId === setupId && levelTableCache.fingerprint === fingerprint) {
    return levelTableCache.rows;
  }

  const stored = readJSON(levelTableStorageKey(setupId), null);
  if (stored && stored.fingerprint === fingerprint) {
    levelTableCache = { setupId, fingerprint, rows: stored.rows };
    return stored.rows;
  }

  const sight_settings = inputs.sight_settings;
  const sight_bar_position = sight_settings.sight_bar_position;
  const calculated = computedResult.value;

  const rows = calc.levelMarksTable(sight_settings, sight_bar_position, calculated);

  levelTableCache = { setupId, fingerprint, rows };
  writeJSON(levelTableStorageKey(setupId), { fingerprint, rows });
  return rows;
}

// Linear interpolation between the two getLevelMarksTable() rows bracketing
// distanceM — see that function's own comment for why 0.25m steps make
// this accurate enough for any consumer's own resolution. Returns null for
// a distance outside the table's own range (before its first row, or past
// wherever calculateMark's physics stopped being viable) — same "not
// available" signal a direct calculateMark call would give by throwing.
export function lookupLevelMark(rows, distanceM) {
  if (rows.length === 0) return null;

  const idx = distanceM / LEVEL_TABLE_STEP - 1; // rows[0] is at distance = LEVEL_TABLE_STEP
  if (idx < 0 || idx > rows.length - 1) return null;

  const i0 = Math.min(rows.length - 2, Math.floor(idx));
  const i1 = i0 + 1;
  const a = rows[i0], b = rows[i1];
  const frac = (distanceM - a.distance) / (b.distance - a.distance);
  const lerp = (key) => a[key] + (b[key] - a[key]) * frac;

  return {
    distance: distanceM,
    mark: lerp('mark'),
    angle: lerp('angle'),
    fps: lerp('fps'),
    t: lerp('t'),
    drop: lerp('drop'),
    verticalMarks: lerp('verticalMarks'),
    horizontalMarks: lerp('horizontalMarks'),
  };
}

// Wait for two animation frames: nextTick() only flushes Vue's virtual DOM
// into the real DOM, it doesn't guarantee the browser has actually painted
// it yet. One rAF fires before the next paint; a second one guarantees that
// paint has already happened by the time it fires.
function nextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

async function recompute() {
  if (isComputing.value) return;

  // The setup this call was actually requested for — captured before the
  // waits below, since a setup switch (loadSetup) can happen while this is
  // pending. Without this check, a switch landing in that window would let
  // compute() run against whichever setup is current *by the time it fires*
  // instead of the one the user asked to recompute, and the persistence
  // watcher would silently attribute the result to that later setup —
  // dropping the original request rather than corrupting anything, but
  // leaving the setup the user actually clicked "Recompute" on still stale.
  const targetSetupId = currentSetupId;

  isComputing.value = true;
  await nextTick();
  await nextPaint();

  if (currentSetupId === targetSetupId) compute();

  isComputing.value = false;
}

// Resolves once no compute is in flight — sync.js awaits this before
// reading the active setup's calc to push. A compute can take multiple
// real seconds for some inputs (calc.js's bisection search), so a sync
// triggered mid-compute — logging out, the debounced auto-sync, "Sync now"
// — would otherwise read local storage's still-stale pre-compute value and
// push exactly that: not a desync (the server ends up matching local
// storage faithfully), just synced a few seconds too early relative to the
// compute actually landing.
export function waitForComputeIdle() {
  if (!isComputing.value) return Promise.resolve();
  return new Promise((resolve) => {
    const stop = watch(isComputing, (computing) => {
      if (!computing) {
        stop();
        resolve();
      }
    });
  });
}

// Swaps `inputs`/`computedResult` over to a different setup's data, and
// re-points the persistence watcher above at that setup's record. Called
// once on startup (setups.js's initSetups()) and again on every setup
// switch. Any in-flight compute error doesn't carry across setups — it
// describes the old setup's inputs, which are gone now. Undo/redo history
// DOES carry, per setup (see saveUndoHistory) — switching away and back
// resumes right where it was left, it just isn't live in memory for a
// setup that isn't the active one.
export function loadSetup(setupId, fallbackName = 'Default') {
  clearTimeout(undoDebounceTimer);

  currentSetupId = setupId;

  // Every id setups.js hands here should already have a record (createSetup/
  // applySetupData both populate one eagerly) — this fallback is just a
  // defensive backstop, not the everyday path it used to be.
  let record = store.value.setups[setupId];
  if (!record) {
    record = createDefaultRecord(fallbackName);
    store.value.setups[setupId] = record;
  }

  // Loading a setup's own already-correct timestamp must not itself count
  // as a fresh edit — see suppressAutoTouch's own comment. Cleared on
  // nextTick rather than synchronously right after, since the auto-touch
  // watcher below is deep and async: it wouldn't actually run (and see
  // this flag) until after this function has already returned.
  suppressAutoTouch = true;
  inputs.sight_settings = calc.SightSettings.fromJson(record.sight_settings);
  inputs.marks = calc.Marks.fromJson(record.marks).marks;
  inputs.description = record.description ?? '';

  const fresh = isCalcFresh(record.calc);
  computedResult.value = fresh ? calc.Calculated.fromJson(record.calc) : new calc.Calculated();
  isStale.value = !fresh;
  setupUpdatedAt.value = record.updated_at ?? 0;
  backfillTimestamps(inputs);
  computeError.value = null;
  nextTick(() => { suppressAutoTouch = false; });

  // Switching setups (or loading one after login) never runs compute() here,
  // even when it loads stale — that's a synchronous, potentially slow solve
  // (calc.js's bisection search) that would block the switch itself on the
  // main thread, which is exactly backwards for something that should just
  // be swapping which setup's already-known data is on screen. A setup
  // pulled from the server or imported from a file now carries its sender's
  // own already-computed fit (see exportSetupJson/applySetupData), so this
  // is only ever actually stale for a setup that genuinely has never been
  // computed against these inputs by anyone — which surfaces as the
  // ordinary "click Recompute" prompt, same as any other stale edit.
  lastComputedSnapshot = isStale.value ? null : snapshotInputs();
  lastCommittedSnapshot = snapshotInputs();

  undoStack = record.undo?.stack ?? [];
  redoStack = record.undo?.redo ?? [];
  canUndo.value = undoStack.length > 0;
  canRedo.value = redoStack.length > 0;

  setupLoadedListeners.forEach((fn) => fn(setupId));
}

// Export/import cover sight settings + marks only — everything else
// (velocity, drag, fit) is derived from those and gets recalculated on
// load, so there's nothing else worth round-tripping through a file.
//
// Takes a setupId so a row in the Setups panel can export/import any setup,
// not just whichever one happens to be active: the active setup's data
// lives in the live `inputs`, but any other setup only exists in the store
// right now, so this branches on whether setupId matches currentSetupId.
// name/updated_at are included so sync.js can read a setup's name and its
// own last-changed timestamp through this same function rather than
// parallel ones — a plain file export/import round-trip ignores both
// (parseSetupImport below only ever reads sight_settings/marks back out),
// so they're harmless extra metadata there, not something the export
// format actually depends on.
export function exportSetupJson(setupId) {
  const record = setupId === currentSetupId
    ? { name: store.value.setups[setupId]?.name, sight_settings: inputs.sight_settings, marks: inputs.marks, description: inputs.description, updated_at: setupUpdatedAt.value }
    : store.value.setups[setupId];

  return JSON.stringify({
    name: record.name,
    sight_settings: record.sight_settings,
    marks: record.marks,
    description: record.description ?? '',
    updated_at: record.updated_at,
  }, null, 2);
}

// Sync-only accessor for a setup's already-computed fit (including which
// CALCULATOR_VERSION it was computed with) — deliberately separate from
// exportSetupJson, which is also the plain file-export format and never
// carries calc (see that function's own comment): a portable setup file is
// just sight_settings/marks, recomputed fresh on import.
export function getSetupCalc(id) {
  if (id === currentSetupId) {
    return isStale.value ? null : computedResult.value.toJSON();
  }
  return store.value.setups[id]?.calc ?? null;
}

// Validates and parses an exported JSON string into model instances —
// split out from applySetupData() so a caller (the Setups panel's "Import
// Setup" button) can validate a file *before* deciding to create a new
// setup from it, rather than creating one first and leaving an empty
// orphan behind if the file turns out to be bad.
export function parseSetupImport(json) {
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Not valid JSON.');
  }
  if (!parsed || typeof parsed !== 'object' || !parsed.sight_settings || !Array.isArray(parsed.marks)) {
    throw new Error('Missing "sight_settings" or "marks" — this doesn\'t look like a sight marks export.');
  }

  // Validate/normalize through the model classes — a bad Mark should
  // reject the whole import rather than land partially applied.
  return {
    sight_settings: calc.SightSettings.fromJson(parsed.sight_settings),
    marks: calc.Marks.fromJson(parsed.marks).marks,
    description: parsed.description ?? '',
  };
}

// Applies already-parsed setup data to a setup by id — the live `inputs` if
// it's the currently active setup, otherwise straight into the store
// record (creating one if this id has never been seen before — see
// sync.js's pullAll, which relies on this to register a setup pulled from
// the server for the first time, with no separate "add the id" step
// needed). `calc` is optional: a plain file import (parseSetupImport) never
// has a fit worth carrying (see exportSetupJson's own comment) and simply
// omits it, leaving staleness to be recalculated the normal way from
// whatever sight_settings/marks just changed to; a pull from the server
// (sync.js) always passes its own already-computed fit (or explicitly
// null), carrying over whichever device last computed it so this setup
// doesn't need its own recompute the first time it's opened locally.
export function applySetupData(setupId, { name, sight_settings, marks, description, updated_at, calc: calcValue } = {}) {
  if (setupId === currentSetupId) {
    if (name != null) {
      const record = store.value.setups[setupId];
      if (record) record.name = name;
    }
    // A caller passing its own updated_at (sync.js pulling a remote setup)
    // is handing us an authoritative timestamp to adopt as-is — the
    // auto-touch watcher must not then stamp its own Date.now() over it.
    // A plain file import (input_setups.vue) never passes one, and *should*
    // get a fresh touch here: it's a genuine new local change with nothing
    // else to date it by.
    const hasAuthoritativeTimestamp = updated_at != null;
    if (hasAuthoritativeTimestamp) suppressAutoTouch = true;
    Object.assign(inputs.sight_settings, sight_settings);
    // marks may be plain JSON (sync.js pulling a remote setup straight off
    // the wire) rather than actual Mark instances — fromJson is idempotent
    // over already-hydrated Marks too, so normalizing unconditionally here
    // guarantees inputs.marks always has real instances with methods like
    // inM(), rather than only working by luck when a caller happened to
    // pre-hydrate (see the graph/inM() panel bug this was causing).
    inputs.marks.splice(0, inputs.marks.length, ...calc.Marks.fromJson(marks).marks);
    inputs.description = description ?? '';
    if (hasAuthoritativeTimestamp) {
      setupUpdatedAt.value = updated_at;
      nextTick(() => { suppressAutoTouch = false; });
    }

    if (calcValue !== undefined) {
      if (calcValue != null) {
        // calcValue is always plain JSON here (sync.js's only caller passes
        // remote.data.calc straight off the wire) rather than an actual
        // Calculated instance — hydrate it the same way loadSetup does,
        // rather than assigning the raw object directly (see the marks
        // splice below, and the inM() bug that exact mistake caused before).
        computedResult.value = calc.Calculated.fromJson(calcValue);
        lastComputedSnapshot = snapshotInputs();
        isStale.value = false;
      } else {
        lastComputedSnapshot = null;
        isStale.value = true;
      }
    }
    // The persistence watcher above mirrors inputs/isStale/computedResult/
    // setupUpdatedAt into store.value.setups[currentSetupId] on its own —
    // nothing to write into the store directly here.
    return;
  }

  const existing = store.value.setups[setupId];
  store.value.setups[setupId] = {
    name: name ?? existing?.name ?? 'Setup',
    sight_settings,
    marks,
    description: description ?? existing?.description ?? '',
    calc: calcValue ?? null,
    updated_at: updated_at ?? existing?.updated_at ?? Date.now(),
    undo: existing?.undo ?? { stack: [], redo: [] },
  };
}

export function useShared() {
  return {
    inputs,
    computedResult,
    isStale,
    computeError,
    isComputing,
    recompute,
    undo,
    redo,
    canUndo,
    canRedo,
    getLevelMarksTable,
    lookupLevelMark,
  };
}
